// ABOUTME: Subscribe to /events/stream while the chat panel is open and
// surface a `publish_completed` notification as a posted-confirmation card in
// the transcript.

import { useEffect, useRef } from 'react'
import { subscribeEventStream } from '@/api/eventStream'
import type { AIChatMessage } from '@/stores/aiChatTypes'
import { useNotificationsStore } from '@/stores/useNotificationsStore'

interface UsePublishConfirmationStreamArgs {
  /** Frontend conversation id (from useAIChatStore). */
  conversationId: string | undefined
  /** addMessage action from useAIChatStore. */
  addMessage: (
    conversationId: string,
    message: Omit<AIChatMessage, 'id' | 'timestamp'>,
    options?: { insertBeforeUnsentTail?: boolean }
  ) => string
}

/**
 * Open an SSE connection to /events/stream while the chat panel is mounted.
 * On each `publish_completed` notification, append a posted-confirmation card
 * to the active conversation.
 */
export function usePublishConfirmationStream({ conversationId, addMessage }: UsePublishConfirmationStreamArgs): void {
  const fetchNotifications = useNotificationsStore((s) => s.fetchNotifications)
  const lastPublishIdRef = useRef<string | null>(null)

  useEffect(() => {
    // Publish confirmations are conversation-scoped, not session-scoped — a
    // publish_completed notification fires regardless of which agent run (if
    // any) created the draft. Surface it whenever the chat panel has an
    // active conversation so the SMB owner sees the loop close.
    if (!conversationId) return
    const unsubscribe = subscribeEventStream({
      onNotification: (notification) => {
        if (notification.type !== 'publish_completed') return
        if (lastPublishIdRef.current === notification.id) return
        lastPublishIdRef.current = notification.id
        const platform = (notification.payload?.platform as string | undefined) ?? 'social'
        const externalUrl = (notification.payload?.external_url as string | undefined) ?? null
        addMessage(
          conversationId,
          {
            role: 'assistant',
            content: '',
            metadata: {
              posted_confirmation: {
                platform,
                post_url: externalUrl,
                posted_at: notification.created_at,
              },
            },
          },
          // A publish confirmation fires independently of whatever
          // turn is on screen — anchor it above any user message still
          // sitting unsent at the tail instead of appending past it.
          { insertBeforeUnsentTail: true }
        )
        void fetchNotifications()
      },
      onError: (err) => {
        console.warn('Notifications stream error:', err)
      },
    })
    return unsubscribe
  }, [conversationId, addMessage, fetchNotifications])
}
